import React, { useContext, useEffect, useState } from "react";
import { IconButton } from "@mui/material";
import { ChevronLeft, ChevronRight, Close } from "@mui/icons-material";
import { ComponentComp } from "../GameComponent";
import { TableContext, TableContextType } from "../tableContext";
import { GameComponentForGame } from "../../implementationTypes";
import "./DeckContentPanel.css";

export function DeckContentPanel(props: { containerId: string }) {
  const { tableState, tableId, decks, selectedComponent, setOpenDeckContentId } = useContext(
    TableContext as React.Context<TableContextType>,
  );
  const [collapsed, setCollapsed] = useState(false);
  const [drawCards, setDrawCards] = useState<GameComponentForGame[]>([]);
  const [discardCards, setDiscardCards] = useState<GameComponentForGame[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch("/api/container-content?tableId=" + tableId + "&containerId=" + encodeURIComponent(props.containerId))
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        setDrawCards((data.components ?? []) as GameComponentForGame[]);
        setDiscardCards((data.discardedComponents ?? []) as GameComponentForGame[]);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Error fetching deck content:", err);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [props.containerId, tableId, tableState]);

  const deck = tableState.getGameComponentById(props.containerId);
  const container = tableState.containers[props.containerId];
  const title = deck?.options?.label ?? deck?.name ?? props.containerId;
  const drawCount = container?.componentAmt ?? drawCards.length;
  const discardCount = container?.discardSize ?? discardCards.length;
  const cardInfo = decks[deck?.deckName ?? drawCards[0]?.options?.deckName ?? ""]?.cardInfo;
  const cardWidth = Math.min(Math.max(100, (cardInfo?.w ?? 280) * 0.2), 180);
  const cardHeight = Math.min(Math.max(150, (cardInfo?.h ?? 400) * 0.2), 270);
  const collapsing = collapsed || !!selectedComponent;

  const renderSection = (label: string, cards: GameComponentForGame[], discard: boolean) => (
    <div className="deck-content-panel-section">
      <div className="deck-content-panel-label">
        {label}
        {cards.length === 0 && " (empty)"}
      </div>
      <div className="deck-content-panel-cards">
        {cards.map((card) => (
          <div className="deck-content-panel-card" key={card.id} style={{ width: cardWidth, height: cardHeight }}>
            <ComponentComp
              data={card}
              x={50}
              y={50}
              width={cardWidth}
              height={cardHeight}
              clickable={false}
              deckContentDisplay={true}
              deckDisplayDiscard={discard}
            />
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className={"deck-content-panel" + (collapsing ? " collapsed" : "")} data-id={props.containerId}>
      <div className="deck-content-panel-header">
        <span className="deck-content-panel-title">{title}</span>
        <span className="deck-content-panel-counts">
          {drawCount} draw · {discardCount} discard
        </span>
        <IconButton
          size="small"
          aria-label="Close"
          onClick={(evt) => {
            evt.stopPropagation();
            setOpenDeckContentId(null);
          }}
        >
          <Close />
        </IconButton>
        <IconButton
          size="small"
          aria-label={collapsing ? "Expand" : "Collapse"}
          onClick={(evt) => {
            evt.stopPropagation();
            setCollapsed((c) => !c);
          }}
        >
          {collapsing ? <ChevronRight /> : <ChevronLeft />}
        </IconButton>
      </div>
      <div className="deck-content-panel-body">
        {loading ? (
          "Fetching deck content..."
        ) : (
          <>
            {renderSection("Draw Pile", drawCards, false)}
            {renderSection("Discard Pile", discardCards, true)}
          </>
        )}
      </div>
    </div>
  );
}
